/**
 * Mapper: team-message.mapper.ts
 *
 * Transforms raw Supabase row objects (snake_case) into UI types (camelCase)
 * for team_conversations, team_messages, and org member profiles.
 *
 * Pattern follows src/mappers/issue.mapper.ts — pure functions, no side effects,
 * no Supabase calls. Called exclusively from src/services/team-message.service.ts.
 */

import type {
  TeamConversation,
  TeamConversationMember,
  TeamMessage,
  TeamMessageAttachment,
  OrgMember,
  SystemUpdateMetadata,
} from '@/types/teamMessages';

// ── Helpers ───────────────────────────────────────────────────────────────────

function toInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function safeString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

// ── DB → UI: Conversation ─────────────────────────────────────────────────────

/**
 * Maps a raw team_conversations row to a TeamConversation.
 * Members, lastMessage, and unreadCount are enriched by the service layer
 * and passed in rather than derived here to keep the mapper pure.
 */
export function toUiConversation(
  row: Record<string, unknown>,
  members: TeamConversationMember[],
  lastMessage: TeamMessage | null,
  unreadCount: number,
): TeamConversation {
  return {
    id: safeString(row.id),
    organizationId: safeString(row.organization_id),
    type: (row.type as 'dm' | 'group') ?? 'dm',
    name: row.name != null ? safeString(row.name) : null,
    createdBy: row.created_by != null ? safeString(row.created_by) : null,
    createdAt: safeString(row.created_at),
    updatedAt: safeString(row.updated_at),
    members,
    lastMessage,
    unreadCount,
  };
}

// ── DB → UI: Conversation member ──────────────────────────────────────────────

/**
 * Maps a team_conversation_members row (with an embedded profiles join) to a
 * TeamConversationMember. Expects `row.profiles` to be the joined profile object.
 */
export function toUiConversationMember(row: Record<string, unknown>): TeamConversationMember {
  const profile = (row.profiles ?? {}) as Record<string, unknown>;
  const name = safeString(profile.full_name ?? profile.email, 'Unknown');
  return {
    userId: safeString(row.user_id),
    displayName: name,
    initials: toInitials(name),
    role: safeString(profile.role),
    joinedAt: safeString(row.joined_at),
  };
}

// ── DB → UI: Message ─────────────────────────────────────────────────────────

/**
 * Maps a raw team_messages row to a TeamMessage.
 * Requires the current user's ID to set `isOwn`, and the resolved sender name
 * (from a joined profiles row) to be passed in by the service.
 */
export function toUiMessage(
  row: Record<string, unknown>,
  currentUserId: string,
  senderName: string,
): TeamMessage {
  const rawAttachments = Array.isArray(row.attachments) ? row.attachments : [];
  return {
    id: safeString(row.id),
    conversationId: safeString(row.conversation_id),
    senderId: safeString(row.sender_id),
    senderName,
    senderInitials: toInitials(senderName),
    body: safeString(row.body),
    attachments: rawAttachments as TeamMessageAttachment[],
    createdAt: safeString(row.created_at),
    isOwn: safeString(row.sender_id) === currentUserId,
    isSystem:  (row as any).is_system ?? false,
    metadata:  (row as any).metadata  as SystemUpdateMetadata | undefined ?? undefined,
  };
}

// ── DB → UI: Org member ───────────────────────────────────────────────────────

/**
 * Maps a profiles row to an OrgMember, used to populate @ mention lists
 * and new conversation member pickers.
 */
export function toUiOrgMember(row: Record<string, unknown>): OrgMember {
  const name = safeString(row.full_name ?? row.email, 'Unknown');
  // Prefer auth_user_id as userId since team_conversation_members.user_id
  // stores auth UIDs (auth.uid()). Falls back to profile id.
  const userId = safeString(row.auth_user_id ?? row.id);
  return {
    userId,
    displayName: name,
    initials: toInitials(name),
    role: safeString(row.role),
  };
}
